/**
 * Admin Dashboard – system-level control for Seedion (oversight, troubleshooting, user role management).
 * SuperAdmin only. Same layout as Manager/Employee: sidebar + main content.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import hrApi from './api';
import WorkingHoursCalendar from './components/WorkingHoursCalendar';
import ManagerDashboard from './ManagerDashboard';
import { formatDisplayName } from './utils/formatTime';
import './css/EmployeeDashboard.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const TAB_OVERVIEW = 'overview';
const TAB_USERS = 'users';
const TAB_WORKING_HOURS = 'working-hours';
const TAB_AUDIT = 'audit';
const TAB_EMAIL = 'email-log';

const ADMIN_VIEW_ADMIN = 'admin';
const ADMIN_VIEW_MANAGER = 'manager';

function AdminDashboard() {
  const navigate = useNavigate();
  const [adminViewMode, setAdminViewMode] = useState(ADMIN_VIEW_ADMIN);
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditSkip, setAuditSkip] = useState(0);
  const [auditLimit, setAuditLimit] = useState(50);
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [auditLoading, setAuditLoading] = useState(false);
  const [emailEntries, setEmailEntries] = useState([]);
  const [emailTotal, setEmailTotal] = useState(0);
  const [emailSkip, setEmailSkip] = useState(0);
  const [emailLimit, setEmailLimit] = useState(50);
  const [emailStatusFilter, setEmailStatusFilter] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingRoleForId, setUpdatingRoleForId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [calendarEmployeeId, setCalendarEmployeeId] = useState('');
  const [runningPrefill, setRunningPrefill] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ username: '', email: '', password: '', role: 'employee' });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [roleByUserId, setRoleByUserId] = useState({});
  const [me, setMe] = useState(null);
  const [editUsernameUser, setEditUsernameUser] = useState(null);
  const [editUsernameValue, setEditUsernameValue] = useState('');
  const [editUsernameLoading, setEditUsernameLoading] = useState(false);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    setError('');
    try {
      const res = await hrApi.get('/api/admin/health');
      setHealth(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load system health');
      setHealth({ ok: false, backend: 'error', database: 'error' });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setError('');
    try {
      const res = await hrApi.get('/api/admin/users');
      setUsers(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchAudit = useCallback(async () => {
    setAuditLoading(true);
    setError('');
    try {
      const params = { limit: auditLimit, skip: auditSkip };
      if (auditActionFilter) params.action = auditActionFilter;
      const res = await hrApi.get('/api/admin/audit-log', { params });
      setAuditEntries(res.data.entries);
      setAuditTotal(res.data.total);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load audit log');
      setAuditEntries([]);
    } finally {
      setAuditLoading(false);
    }
  }, [auditSkip, auditLimit, auditActionFilter]);

  const fetchEmailLog = useCallback(async () => {
    setEmailLoading(true);
    setError('');
    try {
      const params = { limit: emailLimit, skip: emailSkip };
      if (emailStatusFilter) params.status = emailStatusFilter;
      const res = await hrApi.get('/api/admin/email-log', { params });
      setEmailEntries(res.data.entries);
      setEmailTotal(res.data.total);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 404) {
        setError(
          'Email log endpoint was not found. Restart the Staff Portal backend so it loads the latest server code, then refresh this page.'
        );
      } else {
        setError(data?.message || 'Failed to load email log');
      }
      setEmailEntries([]);
    } finally {
      setEmailLoading(false);
    }
  }, [emailSkip, emailLimit, emailStatusFilter]);

  const fetchEmployees = useCallback(async () => {
    setEmployeesLoading(true);
    setError('');
    try {
      const res = await hrApi.get('/api/employees');
      const list = Array.isArray(res.data) ? res.data : [];
      setEmployees(list);
      setCalendarEmployeeId((prev) => (prev && list.some((e) => e._id === prev) ? prev : (list[0]?._id || '')));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load employees');
      setEmployees([]);
      setCalendarEmployeeId('');
    } finally {
      setEmployeesLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    hrApi.get('/api/me').then((res) => { if (!cancelled) setMe(res.data); }).catch(() => { if (!cancelled) setMe(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeTab === TAB_OVERVIEW) fetchHealth();
  }, [activeTab, fetchHealth]);

  useEffect(() => {
    if (activeTab === TAB_USERS) fetchUsers();
  }, [activeTab, fetchUsers]);

  useEffect(() => {
    if (users.length > 0) {
      setRoleByUserId((prev) => {
        const next = { ...prev };
        users.forEach((u) => { next[u._id] = u.role; });
        return next;
      });
    }
  }, [users]);

  useEffect(() => {
    if (activeTab === TAB_AUDIT) fetchAudit();
  }, [activeTab, auditSkip, auditLimit, auditActionFilter, fetchAudit]);

  useEffect(() => {
    if (activeTab === TAB_EMAIL) fetchEmailLog();
  }, [activeTab, emailSkip, emailLimit, emailStatusFilter, fetchEmailLog]);

  useEffect(() => {
    if (activeTab === TAB_WORKING_HOURS) fetchEmployees();
  }, [activeTab, fetchEmployees]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login', { replace: true });
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRoleForId(userId);
    setError('');
    try {
      await hrApi.patch(`/api/admin/users/${userId}/role`, { role: newRole });
      await fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update role');
    } finally {
      setUpdatingRoleForId(null);
    }
  };

  const handleRunPrefillToday = async () => {
    setRunningPrefill(true);
    setError('');
    try {
      await hrApi.post('/api/admin/prefill/today');
      setCalendarRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to run prefill for today');
    } finally {
      setRunningPrefill(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setCreateUserLoading(true);
    try {
      await hrApi.post('/api/admin/users', createUserForm);
      setShowCreateUserModal(false);
      setCreateUserForm({ username: '', email: '', password: '', role: 'employee' });
      await fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const openEditUsername = (u) => {
    setEditUsernameUser(u);
    setEditUsernameValue(u.username || '');
    setError('');
  };

  const handleSaveUsername = async (e) => {
    e.preventDefault();
    if (!editUsernameUser || !editUsernameValue.trim()) return;
    setError('');
    setEditUsernameLoading(true);
    try {
      await hrApi.patch(`/api/admin/users/${editUsernameUser._id}`, { username: editUsernameValue.trim() });
      setEditUsernameUser(null);
      setEditUsernameValue('');
      await fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update username');
    } finally {
      setEditUsernameLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleString();
  };

  const formatEmailCategory = (c) => {
    if (!c) return '—';
    if (c === 'holiday_new_request') return 'Holiday: new request';
    if (c === 'holiday_decision') return 'Holiday: decision';
    return c;
  };

  const emailStatusBadgeClass = (s) => {
    if (s === 'sent') return 'bg-success';
    if (s === 'failed') return 'bg-danger';
    return 'bg-secondary';
  };

  const formatEmailProvider = (provider) => {
    if (provider === 'ses') return 'SES';
    if (provider === 'postmark') return 'Postmark';
    return provider || '—';
  };

  const providerAttemptBadgeClass = (status) => {
    if (status === 'sent') return 'bg-success';
    if (status === 'failed') return 'bg-danger';
    return 'bg-secondary';
  };

  const renderProviderAttempts = (row) => {
    const attempts = Array.isArray(row.providerAttempts) ? row.providerAttempts : [];
    if (attempts.length === 0) {
      return row.provider ? (
        <span className="badge bg-secondary">{formatEmailProvider(row.provider)}</span>
      ) : '—';
    }

    return (
      <div className="d-flex flex-column gap-1">
        {attempts.map((attempt, index) => (
          <div key={`${attempt.provider || 'provider'}-${index}`} className="small">
            <div className="d-flex flex-wrap gap-1 align-items-center">
              <span className="badge bg-secondary">{formatEmailProvider(attempt.provider)}</span>
              {attempt.role && <span className="text-muted">{attempt.role}</span>}
              <span className={`badge ${providerAttemptBadgeClass(attempt.status)}`}>
                {attempt.status}
              </span>
            </div>
            {attempt.failureReason && (
              <div className="text-danger text-truncate" title={attempt.failureReason}>
                {attempt.failureReason}
              </div>
            )}
            {attempt.providerMessageId && (
              <div className="text-muted text-truncate" title={attempt.providerMessageId}>
                Message-ID: {attempt.providerMessageId}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (adminViewMode === ADMIN_VIEW_MANAGER) {
    return (
      <ManagerDashboard
        isAdminView
        onSwitchToAdmin={() => setAdminViewMode(ADMIN_VIEW_ADMIN)}
      />
    );
  }

  return (
    <div className="employee-dashboard">
      <nav className="hr-sidebar">
        <div className="hr-sidebar-logo">
          <span className="hr-sidebar-logo-mark" aria-label="Staff Portal">SP</span>
          <span className="hr-sidebar-logo-text">Staff Portal</span>
        </div>
        <div className="hr-sidebar-view-toggle">
          <button
            type="button"
            className={`btn btn-sm w-50 ${adminViewMode === ADMIN_VIEW_ADMIN ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setAdminViewMode(ADMIN_VIEW_ADMIN)}
          >
            <i className="bi bi-shield-lock me-1" /> Admin
          </button>
          <button
            type="button"
            className={`btn btn-sm w-50 ${adminViewMode === ADMIN_VIEW_MANAGER ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setAdminViewMode(ADMIN_VIEW_MANAGER)}
          >
            <i className="bi bi-people me-1" /> Manager
          </button>
        </div>
        <ul className="nav flex-column">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeTab === TAB_OVERVIEW ? 'active' : ''}`}
              onClick={() => setActiveTab(TAB_OVERVIEW)}
            >
              <i className="bi bi-heart-pulse" /> System health
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeTab === TAB_USERS ? 'active' : ''}`}
              onClick={() => setActiveTab(TAB_USERS)}
            >
              <i className="bi bi-people" /> User roles
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeTab === TAB_WORKING_HOURS ? 'active' : ''}`}
              onClick={() => setActiveTab(TAB_WORKING_HOURS)}
            >
              <i className="bi bi-calendar3" /> Working hours
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeTab === TAB_AUDIT ? 'active' : ''}`}
              onClick={() => setActiveTab(TAB_AUDIT)}
            >
              <i className="bi bi-journal-text" /> Audit log
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link w-100 text-start border-0 ${activeTab === TAB_EMAIL ? 'active' : ''}`}
              onClick={() => setActiveTab(TAB_EMAIL)}
            >
              <i className="bi bi-envelope-check" /> Email log
            </button>
          </li>
        </ul>
      </nav>

      <main className="hr-main">
        <div className="hr-main-topbar d-flex align-items-center justify-content-between px-3 py-2">
          <span className="hr-view-label">{formatDisplayName(me?.username) || 'User'} (Admin)</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
            <i className="bi bi-box-arrow-right me-1" /> Logout
          </button>
        </div>
        <div className="hr-outlet">
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          {activeTab === TAB_OVERVIEW && (
            <>
              <h2 className="hr-page-title mb-3">System health</h2>
              {healthLoading && <p className="text-muted">Checking…</p>}
              {!healthLoading && health && (
                <div className="hr-section-card">
                  <div className="d-flex flex-wrap gap-3">
                    <div className={`p-3 rounded ${health.backend === 'ok' ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`}>
                      <strong className="d-block text-muted small">Backend</strong>
                      <span className={health.backend === 'ok' ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                        {health.backend === 'ok' ? 'OK' : 'Error'}
                      </span>
                    </div>
                    <div className={`p-3 rounded ${health.database === 'connected' ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`}>
                      <strong className="d-block text-muted small">Database</strong>
                      <span className={health.database === 'connected' ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                        {health.database === 'connected' ? 'Connected' : health.database || 'Error'}
                      </span>
                    </div>
                    {health.databaseName && (
                      <div className="p-3 rounded bg-light">
                        <strong className="d-block text-muted small">Database name</strong>
                        <span>{health.databaseName}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === TAB_USERS && (
            <>
              <h2 className="hr-page-title mb-2">User role management</h2>
              <p className="text-muted small mb-3">
                Create users or change a user&apos;s role: Staff, Manager, or Assistant Manager. Use the dropdown and Apply.
              </p>
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => { setShowCreateUserModal(true); setError(''); }}
                >
                  Create user
                </button>
              </div>
              {showCreateUserModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Create user</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => { setShowCreateUserModal(false); setCreateUserForm({ username: '', email: '', password: '', role: 'employee' }); setError(''); }} />
                      </div>
                      <form onSubmit={handleCreateUser}>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Username</label>
                            <input
                              type="text"
                              className="form-control"
                              value={createUserForm.username}
                              onChange={(e) => setCreateUserForm((f) => ({ ...f, username: e.target.value }))}
                              required
                              placeholder="Display name"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Email</label>
                            <input
                              type="email"
                              className="form-control"
                              value={createUserForm.email}
                              onChange={(e) => setCreateUserForm((f) => ({ ...f, email: e.target.value }))}
                              required
                              placeholder="user@example.com"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Password</label>
                            <input
                              type="password"
                              className="form-control"
                              value={createUserForm.password}
                              onChange={(e) => setCreateUserForm((f) => ({ ...f, password: e.target.value }))}
                              required
                              minLength={6}
                              placeholder="Min 6 characters"
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Role</label>
                            <select
                              className="form-select"
                              value={createUserForm.role}
                              onChange={(e) => setCreateUserForm((f) => ({ ...f, role: e.target.value }))}
                            >
                              <option value="employee">Staff</option>
                              <option value="manager">Manager</option>
                            </select>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowCreateUserModal(false); setError(''); }}>
                            Cancel
                          </button>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={createUserLoading}>
                            {createUserLoading ? 'Creating…' : 'Create user'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
              {editUsernameUser && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Edit username</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => { setEditUsernameUser(null); setEditUsernameValue(''); setError(''); }} />
                      </div>
                      <form onSubmit={handleSaveUsername}>
                        <div className="modal-body">
                          <p className="text-muted small mb-2">User: {editUsernameUser.email}</p>
                          <div className="mb-0">
                            <label className="form-label">Username</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editUsernameValue}
                              onChange={(e) => setEditUsernameValue(e.target.value)}
                              required
                              placeholder="Display name"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditUsernameUser(null); setEditUsernameValue(''); setError(''); }}>
                            Cancel
                          </button>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={editUsernameLoading || !editUsernameValue.trim()}>
                            {editUsernameLoading ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
              {usersLoading && <p className="text-muted">Loading users…</p>}
              {!usersLoading && users.length > 0 && (
                <div className="hr-allowance-table-wrap">
                  <table className="hr-allowance-table table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Current role</th>
                        <th>Change role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u._id}>
                          <td className="hr-allowance-name">
                            {formatDisplayName(u.username)}
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 ms-2 text-secondary"
                              onClick={() => openEditUsername(u)}
                              title="Edit username"
                              aria-label={`Edit username for ${u.email}`}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                          </td>
                          <td>{u.email}</td>
                          <td>
                            <span className={`badge ${u.role === 'superadmin' ? 'bg-warning text-dark' : u.role === 'manager' ? 'bg-primary' : u.role === 'assistant_manager' ? 'bg-info' : 'bg-secondary'}`}>
                              {u.role === 'superadmin' ? 'Admin' : u.role === 'manager' ? 'Manager' : u.role === 'assistant_manager' ? 'Assistant Manager' : 'Staff'}
                            </span>
                          </td>
                          <td>
                            {u.role === 'superadmin' ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <>
                                <select
                                  className="form-select form-select-sm d-inline-block w-auto"
                                  value={roleByUserId[u._id] ?? u.role}
                                  aria-label={`Change role for ${u.email}`}
                                  onChange={(e) => setRoleByUserId((prev) => ({ ...prev, [u._id]: e.target.value }))}
                                >
                                  <option value="employee">Staff</option>
                                  <option value="manager">Manager</option>
                                  <option value="assistant_manager">Assistant Manager</option>
                                </select>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary ms-2"
                                  disabled={updatingRoleForId === u._id}
                                  onClick={() => {
                                    const newRole = roleByUserId[u._id] ?? u.role;
                                    if (newRole !== u.role) handleRoleChange(u._id, newRole);
                                  }}
                                >
                                  {updatingRoleForId === u._id ? 'Updating…' : 'Apply'}
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!usersLoading && users.length === 0 && <p className="text-muted">No users found.</p>}
            </>
          )}

          {activeTab === TAB_WORKING_HOURS && (
            <>
              <h2 className="hr-page-title mb-2">Working hours</h2>
              <p className="text-muted small mb-3">
                Review employee calendars here and run the daily prefill manually for today when you need to verify the setup.
              </p>
              <div className="hr-section-card mb-3">
                <div className="d-flex flex-wrap gap-3 align-items-end">
                  <div>
                    <label className="form-label small mb-1">Employee</label>
                    <select
                      className="form-select"
                      style={{ minWidth: 260 }}
                      value={calendarEmployeeId}
                      onChange={(e) => setCalendarEmployeeId(e.target.value)}
                      disabled={employeesLoading || employees.length === 0}
                    >
                      {employees.length === 0 ? (
                        <option value="">No employees found</option>
                      ) : (
                        employees.map((emp) => (
                          <option key={emp._id} value={emp._id}>
                            {formatDisplayName(emp.username || emp.email)} {emp.role === 'manager' ? '(Manager)' : ''}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleRunPrefillToday}
                      disabled={runningPrefill}
                    >
                      {runningPrefill ? 'Running…' : 'Run prefill for today'}
                    </button>
                  </div>
                </div>
              </div>
              {employeesLoading ? (
                <p className="text-muted">Loading employees…</p>
              ) : calendarEmployeeId ? (
                <WorkingHoursCalendar
                  key={`${calendarEmployeeId}-${calendarRefreshKey}`}
                  adminId={calendarEmployeeId}
                  applyManagerCorrectionLock
                />
              ) : (
                <p className="text-muted">Select an employee to view the calendar.</p>
              )}
            </>
          )}

          {activeTab === TAB_AUDIT && (
            <>
              <h2 className="hr-page-title mb-2">Audit log</h2>
              <p className="text-muted small mb-3">
                Important system actions (role changes, user updates, etc.) for traceability and troubleshooting.
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                <label htmlFor="audit-action-filter" className="mb-0 small">Action:</label>
                <select
                  id="audit-action-filter"
                  className="form-select form-select-sm w-auto"
                  value={auditActionFilter}
                  onChange={(e) => { setAuditActionFilter(e.target.value); setAuditSkip(0); }}
                >
                  <option value="">All</option>
                  <option value="role_change">Role change</option>
                  <option value="user_create">User create</option>
                  <option value="user_update">User update</option>
                  <option value="settings_change">Settings change</option>
                  <option value="assistant_manager_assignment">Assistant Manager assigned</option>
                  <option value="assistant_manager_removed">Assistant Manager removed</option>
                </select>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={fetchAudit}>
                  Refresh
                </button>
              </div>
              {auditLoading && <p className="text-muted">Loading audit log…</p>}
              {!auditLoading && auditEntries.length > 0 && (
                <>
                  <div className="hr-allowance-table-wrap">
                    <table className="hr-allowance-table table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Action</th>
                          <th>By</th>
                          <th>Target</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditEntries.map((entry) => (
                          <tr key={entry._id}>
                            <td>{formatDate(entry.createdAt)}</td>
                            <td>{entry.action}</td>
                            <td>{entry.performedByEmail || entry.performedBy || '—'}</td>
                            <td>{entry.targetUserEmail || '—'}</td>
                            <td className="small font-monospace text-muted">
                              {entry.details && typeof entry.details === 'object'
                                ? JSON.stringify(entry.details)
                                : entry.details || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
                    <span className="small text-muted">
                      Showing {auditSkip + 1}–{Math.min(auditSkip + auditLimit, auditTotal)} of {auditTotal}
                    </span>
                    <select
                      className="form-select form-select-sm w-auto"
                      value={auditLimit}
                      onChange={(e) => { setAuditLimit(Number(e.target.value)); setAuditSkip(0); }}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={auditSkip === 0}
                      onClick={() => setAuditSkip((s) => Math.max(0, s - auditLimit))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={auditSkip + auditLimit >= auditTotal}
                      onClick={() => setAuditSkip((s) => s + auditLimit)}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
              {!auditLoading && auditEntries.length === 0 && <p className="text-muted">No audit entries.</p>}
            </>
          )}

          {activeTab === TAB_EMAIL && (
            <>
              <h2 className="hr-page-title mb-2">Email log</h2>
              <p className="text-muted small mb-3">
                <strong>Sent</strong> — an email provider accepted the message.
                {' '}
                <strong>Failed</strong> — all configured providers returned errors.
                {' '}
                <strong>Skipped</strong> — nothing was sent (e.g. providers not configured, no recipients).
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                <label htmlFor="email-status-filter" className="mb-0 small">Status:</label>
                <select
                  id="email-status-filter"
                  className="form-select form-select-sm w-auto"
                  value={emailStatusFilter}
                  onChange={(e) => { setEmailStatusFilter(e.target.value); setEmailSkip(0); }}
                >
                  <option value="">All</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="skipped">Skipped</option>
                </select>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={fetchEmailLog}>
                  Refresh
                </button>
              </div>
              {emailLoading && <p className="text-muted">Loading email log…</p>}
              {!emailLoading && emailEntries.length > 0 && (
                <>
                  <div className="hr-allowance-table-wrap">
                    <table className="hr-allowance-table table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Status</th>
                          <th>Category</th>
                          <th>Recipients</th>
                          <th>Subject</th>
                          <th>Provider attempts</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailEntries.map((row) => (
                          <tr key={row._id}>
                            <td>{formatDate(row.createdAt)}</td>
                            <td>
                              <span className={`badge ${emailStatusBadgeClass(row.status)}`}>
                                {row.status}
                              </span>
                            </td>
                            <td className="small">{formatEmailCategory(row.category)}</td>
                            <td className="small">
                              {Array.isArray(row.recipients) && row.recipients.length > 0
                                ? row.recipients.join(', ')
                                : '—'}
                            </td>
                            <td className="small">{row.subject || '—'}</td>
                            <td className="small">{renderProviderAttempts(row)}</td>
                            <td className="small text-muted">
                              {row.failureOrSkipReason || (row.providerMessageId ? `Message-ID: ${row.providerMessageId}` : '—')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
                    <span className="small text-muted">
                      Showing {emailSkip + 1}–{Math.min(emailSkip + emailLimit, emailTotal)} of {emailTotal}
                    </span>
                    <select
                      className="form-select form-select-sm w-auto"
                      value={emailLimit}
                      onChange={(e) => { setEmailLimit(Number(e.target.value)); setEmailSkip(0); }}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={emailSkip === 0}
                      onClick={() => setEmailSkip((s) => Math.max(0, s - emailLimit))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={emailSkip + emailLimit >= emailTotal}
                      onClick={() => setEmailSkip((s) => s + emailLimit)}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
              {!emailLoading && emailEntries.length === 0 && <p className="text-muted">No email log entries yet.</p>}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
